/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import * as AdaptiveCards from 'adaptivecards';
import * as adaptivecardsTemplating from 'adaptivecards-templating';
import * as MarkdownIt from 'markdown-it';
import * as React from 'react';
import { Theme } from '../../../common/enums/Theme';
import { MobileAdaptiveCardComponent } from '../../../mobile/components/MobileAdaptiveCard/MobileAdaptiveCard';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { ErrorPage } from '../../containers/ErrorPage';
import { AdaptiveCardComponent, AdaptiveCardComponentProps } from './AdaptiveCard';
import { setActiveApprovalType } from './elements/ApprovalTypeScope';
import { registerBadgeCardElement } from './elements/BadgeCardElement';
import { registerBorderedContainer } from './elements/BorderedContainer';
import { hostConfigContrast } from './hostConfigs/HostConfigContrast';

import './styles/AdaptiveCard.scss';
import { hostConfigDark } from './hostConfigs/HostConfigDark';
import { hostConfigDefault } from './hostConfigs/HostConfigDefault';

// Registers custom element overrides needed to visually match the Teams-hosted renderer:
// - "Badge", a schema 1.5 element not implemented by the bundled OSS SDK.
// - Container's "showBorder"/"roundedCorners", Teams-native renderer extensions not in the OSS SDK.
registerBadgeCardElement();
registerBorderedContainer();

export interface AdaptiveCardProps {
    approvalType: string;
    themeName: Theme;
    template: any;
    dataPayload: any;
    openUrlHandler: (href: string, id: string, title: string) => void;
    submitHandler: (url: string, data: any) => void;
}

export const AdaptiveCardContainer: React.FC<AdaptiveCardProps> = (props: AdaptiveCardProps): JSX.Element => {
    const { approvalType, themeName, template, dataPayload, submitHandler, openUrlHandler } = props;

    const hostConfig = React.useMemo((): any => {
        switch (themeName) {
            case Theme.Dark:
                return hostConfigDark;
            case Theme.Contrast:
                return hostConfigContrast;
            default:
                return hostConfigDefault;
        }
    }, [themeName]);

    const adaptiveCard = new AdaptiveCards.AdaptiveCard();

    // Scopes the Badge/BorderedContainer overrides (registered globally against the SDK, see
    // registerBadgeCardElement/registerBorderedContainer above) to this parse+render pass so
    // only the Agent and App Approvals card picks up their custom rendering.
    setActiveApprovalType(approvalType);

    try {
        AdaptiveCards.AdaptiveCard.onProcessMarkdown = (
            text: string,
            result: AdaptiveCards.IMarkdownProcessingResult
        ): void => {
            result.outputHtml = MarkdownIt().render(text);
            result.didProcess = true;
        };

        adaptiveCard.onExecuteAction = (action: AdaptiveCards.Action): void => {
            const type = action.getJsonTypeName();
            if (type === 'Action.OpenUrl') {
                openUrlHandler(action.getHref(), action.id, action.title);
            } else if (type === 'Action.Submit') {
                const data = (action as any).data;
                submitHandler(data?.url, data);
            }
        };

        const templatePayload = new adaptivecardsTemplating.Template(template);

        // Expand the template with your `$root` data object
        // This binds it to the data and produces the final Adaptive Card payload
        const cardPayload = templatePayload.expand({
            $root: dataPayload,
        });

        adaptiveCard.hostConfig = new AdaptiveCards.HostConfig(hostConfig);
        adaptiveCard.parse(cardPayload);

        const result = adaptiveCard.render();

        // Remove tab focus
        result.tabIndex = -1;

        const acSelectableElements = result.getElementsByClassName('ac-selectable');
        Array.from(acSelectableElements).forEach((acSelectableElement: Element): void => {
            (acSelectableElement as HTMLElement).style.cursor = 'Pointer';
        });

        // added aria label to links in adaptive card
        const anchorElements = result.getElementsByClassName('ac-anchor');
        Array.from(anchorElements).forEach((anchorElement: Element): void => {
            const anchorTitle = anchorElement.getAttribute('title');
            anchorElement.setAttribute('aria-label', anchorTitle);
        });

        // sets aria-expanded state for expand/collapse buttons
        const expandElements = result.getElementsByClassName('ac-selectable');
        Array.from(expandElements).forEach((expandElement: Element): void => {
            const elementTitle = expandElement.getAttribute('title') ?? expandElement.getAttribute('alt');
            if (elementTitle?.toLowerCase().includes('expand')) {
                expandElement.setAttribute('aria-expanded', 'false');
            } else if (elementTitle?.toLowerCase().includes('collapse')) {
                expandElement.setAttribute('aria-expanded', 'true');
            }
        });

        // setting alt to empty string for decorative images
        const imageElements = result.getElementsByClassName('ac-image');
        Array.from(imageElements).forEach((imageElement: Element): void => {
            const imageAlt = imageElement.getAttribute('alt');
            if (!imageAlt) {
                imageElement.setAttribute('alt', '');
            }
            // addressing expand/collapse buttons that are images instead of selectables
            if (imageAlt?.toLowerCase().includes('expand')) {
                imageElement.setAttribute('aria-expanded', 'false');
            } else if (imageAlt?.toLowerCase().includes('collapse')) {
                imageElement.setAttribute('aria-expanded', 'true');
            }
        });

        const childProps: AdaptiveCardComponentProps = {
            approvalType,
            themeName,
            adaptiveCardResult: result,
        };

        return getAppHost().isMobile() ? (
            <MobileAdaptiveCardComponent {...childProps} />
        ) : (
            <AdaptiveCardComponent {...childProps} />
        );
    } catch (error) {
        return <ErrorPage message={error?.message} />;
    }
};
